import { useEffect, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import {
  useGetAdminRequestsQuery,
  useApproveRequestMutation,
  useRejectRequestMutation,
} from '../../api/requestApi';
import { useGetGroupQuery } from '../../api/groupApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import Badge from '../../components/Badge/Badge';
import s from '../../styles/shared/page.module.css';
import buttons from '../../styles/shared/buttons.module.css';
import table from '../../styles/shared/table.module.css';
import styles from './RequestsPage.module.css';

const STATUSES = ['pending', 'approved', 'rejected'];
const NOTE_MAX_LENGTH = 256;

const TYPE_LABELS = {
  group_create: 'Create group',
  group_update: 'Update group',
  group_enable: 'Enable/disable group',
};

const FIELD_LABELS = {
  name: 'Name',
  tile_image_url: 'Tile image URL',
  terms: 'Terms & conditions',
  channels: 'Channels',
  enabled: 'Enabled',
};

function formatValue(value) {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ') || '—';
  return String(value);
}

function ChangesDialog({ request, onHide }) {
  const groupId = request?.type !== 'group_create' ? request?.group_id : undefined;
  const { data: group } = useGetGroupQuery(groupId, { skip: !groupId });

  const rows = useMemo(() => {
    if (!request) return [];
    const payload = request.payload ?? {};
    return Object.entries(payload)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([key, value]) => ({
        field: FIELD_LABELS[key] ?? key,
        isImage: key === 'tile_image_url',
        isTerms: key === 'terms',
        wasRaw: request.type === 'group_create' ? undefined : group?.[key],
        nowRaw: value,
        was: request.type === 'group_create' ? '—' : formatValue(group?.[key]),
        now: formatValue(value),
      }))
      .filter((row) => request.type === 'group_create' || row.was !== row.now);
  }, [request, group]);

  return (
    <Dialog
      header="Requested changes"
      visible={Boolean(request)}
      onHide={onHide}
      style={{ width: '800px', maxWidth: '90vw', maxHeight: '85vh' }}
      modal
    >
      <div className={table.tableWrapper}>
        <DataTable value={rows} emptyMessage="No changes to show.">
          <Column field="field" header="Field" style={{ width: '35%', wordBreak: 'break-word' }} />
          <Column
            header="Old"
            style={{ width: '32.5%', wordBreak: 'break-word' }}
            body={(row) =>
              row.isImage && row.wasRaw ? (
                <img
                  src={row.wasRaw}
                  alt=""
                  style={{ width: '120px', height: '68px', objectFit: 'cover', borderRadius: '8px' }}
                />
              ) : row.isTerms && row.wasRaw ? (
                <div className={styles.termsPreview} dangerouslySetInnerHTML={{ __html: row.wasRaw }} />
              ) : (
                <span style={{ whiteSpace: 'pre-wrap' }}>{row.was}</span>
              )
            }
          />
          <Column
            header="New"
            style={{ width: '32.5%', wordBreak: 'break-word' }}
            body={(row) =>
              row.isImage && row.nowRaw ? (
                <img
                  src={row.nowRaw}
                  alt=""
                  style={{ width: '120px', height: '68px', objectFit: 'cover', borderRadius: '8px' }}
                />
              ) : row.isTerms && row.nowRaw ? (
                <div className={styles.termsPreview} dangerouslySetInnerHTML={{ __html: row.nowRaw }} />
              ) : (
                <span style={{ whiteSpace: 'pre-wrap' }}>{row.now}</span>
              )
            }
          />
        </DataTable>
      </div>
    </Dialog>
  );
}

export default function RequestsPage() {
  const [statusIndex, setStatusIndex] = useState(0);
  const status = STATUSES[statusIndex];
  const [decisionRequest, setDecisionRequest] = useState(null);
  const [decisionAction, setDecisionAction] = useState(null);
  const [note, setNote] = useState('');
  const [changesRequest, setChangesRequest] = useState(null);
  const showToast = useToast();

  const {
    data: requests,
    isLoading,
    isError,
    error,
  } = useGetAdminRequestsQuery(status);

  const [approveRequest, { isLoading: isApproving }] = useApproveRequestMutation();
  const [rejectRequest, { isLoading: isRejecting }] = useRejectRequestMutation();

  useEffect(() => {
    if (isError) showToast('error', 'Requests', errMsg(error, 'Could not load requests.'));
  }, [isError, error, showToast]);

  function openDecision(request, action) {
    setDecisionRequest(request);
    setDecisionAction(action);
    setNote('');
  }

  function closeDecision() {
    setDecisionRequest(null);
    setDecisionAction(null);
  }

  async function submitDecision() {
    try {
      if (decisionAction === 'approve') {
        await approveRequest({ id: decisionRequest.id, note: note.trim() || undefined }).unwrap();
        showToast('success', 'Requests', 'Request approved.');
      } else {
        await rejectRequest({ id: decisionRequest.id, note: note.trim() || undefined }).unwrap();
        showToast('success', 'Requests', 'Request rejected.');
      }
      closeDecision();
    } catch (err) {
      showToast('error', 'Requests', errMsg(err, 'Could not save decision.'));
    }
  }

  const actionBodyTemplate = (request) =>
    request.status === 'pending' ? (
      <div className={table.actions}>
        <Button
          icon="pi pi-check"
          text
          className={table.iconSuccess}
          onClick={() => openDecision(request, 'approve')}
        />
        <Button
          icon="pi pi-times"
          text
          className={table.iconDanger}
          onClick={() => openDecision(request, 'reject')}
        />
      </div>
    ) : (
      <span className="textMuted">{request.decision_note || '—'}</span>
    );

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1>Requests</h1>
          <p className={`${s.subtitle} textMuted`}>Manager change requests awaiting your decision.</p>
        </div>
      </div>

      <TabView activeIndex={statusIndex} onTabChange={(e) => setStatusIndex(e.index)}>
        {STATUSES.map((st) => (
          <TabPanel key={st} header={st.charAt(0).toUpperCase() + st.slice(1)} />
        ))}
      </TabView>

      <div className={table.tableWrapper}>
        <DataTable
          value={requests ?? []}
          loading={isLoading}
          emptyMessage={`No ${status} requests.`}
          responsiveLayout="stack"
        >
          <Column
            header="Organization"
            className={table.contactCol}
            body={(r) => <div className={table.cellInsider}>{r.organization_name}</div>}
          />
          <Column
            header="Requested by"
            className={`${s.mutedCell} ${table.contactCol}`}
            body={(r) => <div className={table.cellInsider}>{r.requested_by_name}</div>}
          />
          <Column
            header="Type"
            className={table.contactCol}
            body={(r) => (
              <div className={table.cellInsider}>
                <Badge background="var(--color-badge-info-bg)" color="var(--color-primary)">
                  {TYPE_LABELS[r.type] ?? r.type}
                </Badge>
              </div>
            )}
          />
          <Column
            header="Details"
            className={table.contactCol}
            body={(r) => (
              <Button
                label="See changes"
                text
                onClick={() => setChangesRequest(r)}
                style={{ width: 'auto', height: 'auto', whiteSpace: 'nowrap' }}
              />
            )}
          />
          {status !== 'pending' && (
            <Column
              header="Date"
              className={`${s.mutedCell} ${table.contactCol}`}
              body={(r) => (
                <div className={table.cellInsider}>
                  {r.decided_at ? new Date(r.decided_at).toLocaleString() : '—'}
                </div>
              )}
            />
          )}
          <Column
            header={status === 'pending' ? 'Actions' : 'Note'}
            className={table.actionsCol}
            body={actionBodyTemplate}
            style={{ width: status === 'pending' ? '172px' : '220px' }}
          />
        </DataTable>
      </div>

      <Dialog
        header={decisionAction === 'approve' ? 'Approve request' : 'Reject request'}
        visible={Boolean(decisionRequest)}
        onHide={closeDecision}
        style={{ width: '420px' }}
        modal
      >
        <div className="field">
          <label htmlFor="note">Note (optional)</label>
          <InputTextarea
            id="note"
            className="w-full mb-1"
            rows={3}
            maxLength={NOTE_MAX_LENGTH}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="textMuted mb-3" style={{ textAlign: 'right' }}>
            {note.length} / {NOTE_MAX_LENGTH}
          </p>
        </div>
        <Button
          label={decisionAction === 'approve' ? 'Approve' : 'Reject'}
          className={decisionAction === 'approve' ? buttons.saveBtn : buttons.dangerBtn}
          loading={isApproving || isRejecting}
          onClick={submitDecision}
        />
      </Dialog>

      <ChangesDialog request={changesRequest} onHide={() => setChangesRequest(null)} />
    </div>
  );
}
